import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { fetchMe, type MeResponse } from "@/lib/me";
import { getAccessToken, AUTH_CHANGED_EVENT } from "@/lib/auth";

interface AuthState {
  me: MeResponse | null;
  loading: boolean;
  permissions: Set<string>;
}

const AuthContext = createContext<AuthState>({ me: null, loading: false, permissions: new Set() });

export function AuthProvider({ children }: { children: ReactNode }) {
  const [hasToken, setHasToken] = useState(() => Boolean(getAccessToken()));
  useEffect(() => {
    const onChange = () => setHasToken(Boolean(getAccessToken()));
    window.addEventListener("storage", onChange);
    window.addEventListener(AUTH_CHANGED_EVENT, onChange);
    return () => {
      window.removeEventListener("storage", onChange);
      window.removeEventListener(AUTH_CHANGED_EVENT, onChange);
    };
  }, []);
  const { data, isLoading } = useQuery({
    queryKey: ["me"],
    queryFn: fetchMe,
    enabled: hasToken,
    staleTime: 60_000,
  });
  const permissions = new Set(data?.permissions ?? []);
  return (
    <AuthContext.Provider value={{ me: data ?? null, loading: isLoading, permissions }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
